import { FC, useState } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Battery as BatteryIcon, Pencil, Trash2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { Battery } from '@/hooks/useBatteryContainers';

interface BatteryBlockProps {
  battery?: Battery;
  position: number;
  allocatedTo?: string;
  onDrop?: (batteryId: string, leadId: string) => void;
  isDouble?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onAllocate?: () => void;
}

export const BatteryBlock: FC<BatteryBlockProps> = ({ 
  battery, 
  position, 
  allocatedTo, 
  onDrop, 
  isDouble = false,
  onEdit,
  onDelete,
  onAllocate,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);

  // Empty slot
  if (!battery) {
    return (
      <div 
        className="aspect-square rounded border-2 border-dashed border-muted-foreground/20 bg-muted/5"
        title={`Positie ${position}`}
      />
    );
  }

  const getStatusColor = () => {
    switch (battery.status) {
      case 'vrij':
      case 'te_bestellen':
        return 'bg-green-500/80 hover:bg-green-500 border-green-500';
      case 'gereserveerd':
        return 'bg-yellow-500/80 hover:bg-yellow-500 border-yellow-500';
      case 'ingepland':
      case 'geplaatst':
        return 'bg-red-600/80 hover:bg-red-600 border-red-600';
      default:
        return 'bg-muted border-muted-foreground';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (battery.status === 'vrij' || battery.status === 'te_bestellen') {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if ((battery.status === 'vrij' || battery.status === 'te_bestellen') && onDrop) {
      onDrop(battery.id, '');
    }
  };

  const sizeClasses = {
    single: 'w-16 h-16',
    double: 'w-32 h-16',
  };

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild onClick={(e) => e.preventDefault()}>
          <div
            className={cn(
              'relative rounded border-2 transition-all cursor-pointer',
              'flex flex-col items-center justify-center p-1',
              'hover:scale-105 hover:brightness-110',
              sizeClasses[isDouble ? 'double' : 'single'],
              getStatusColor(),
              isDragOver && 'ring-2 ring-primary scale-105'
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <BatteryIcon className="h-4 w-4 text-white" />
            <div className="text-[10px] font-bold text-white">{battery.power_kw}kW</div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top">
          <div className="space-y-2">
            <p className="font-semibold">
              {battery.battery_type?.name || 'Batterij'}
            </p>
            <div className="space-y-1">
              <p className="text-sm">Vermogen: {battery.power_kw} kW</p>
              <p className="text-sm">Capaciteit: {battery.capacity_kwh} kWh</p>
              <p className="text-sm">Status: {battery.status}</p>
              {battery.serial_number && (
                <p className="text-sm">Serienummer: {battery.serial_number}</p>
              )}
              {allocatedTo && (
                <p className="text-sm text-yellow-500">
                  Toegewezen aan: {allocatedTo}
                </p>
              )}
            </div>
            {(onAllocate || onEdit || onDelete) && (
              <div className="flex gap-1 pt-2 border-t flex-wrap">
                {onAllocate && (battery.status === 'vrij' || battery.status === 'te_bestellen') && (
                  <Button size="sm" variant="default" onClick={(e) => { e.stopPropagation(); onAllocate(); }}>
                    <Plus className="h-3 w-3 mr-1" />
                    Toewijzen
                  </Button>
                )}
                {onEdit && (
                  <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); onEdit(); }}>
                    <Pencil className="h-3 w-3 mr-1" />
                    Bewerken
                  </Button>
                )}
                {onDelete && (
                  <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
                    <Trash2 className="h-3 w-3 mr-1" />
                    Verwijderen
                  </Button>
                )}
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
